import store from '@/store';
import { createApolloProvider } from '@vue/apollo-option';
import config from '@/config/client';
import { getInstance } from '@/auth';
import { ApolloClient, from, Operation, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { createUploadLink } from 'apollo-upload-client';
import { getLeaseManager } from '@/license';
import { getMainDefinition, getOperationDefinition } from '@apollo/client/utilities';
import { TypeNode } from 'graphql';
import { SentrySubscriptionLink } from './link-sentry';
import { WebSocketLink } from './link-websocket';
import { errorLink } from './link-error';
import { retryLink } from './link-retry';
import { computeLink, getCache } from './cache';

/**
 * Sets authorization and lease-token header for regular http graphql requests
 * Websocket requests (subscriptions) are special cased in the WebSocketLink
 */
const authLink = setContext(async (_request, { headers }) => {
  const newHeaders: {
    'atlasti-web-app-version': string;
    authorization?: string;
    'lease-token'?: string;
  } = {
    'atlasti-web-app-version': config.appVersion.replace(/–/g, '+'),
  };

  const authInstance = getInstance();

  const leaseManager = getLeaseManager(store);

  const leaseToken = await leaseManager.requestLeaseToken();

  const authToken = await authInstance.getTokenSilently();

  const idToken = authInstance.idToken ?? (await authInstance.getIdTokenClaims())?.__raw;

  if (authToken) {
    newHeaders.authorization = `Bearer ${authToken}`;
  }

  if (leaseToken) {
    newHeaders['lease-token'] = leaseToken;
  }

  if (idToken) {
    newHeaders['id-token'] = idToken;
  }

  return {
    // for the normal graphql link
    headers: {
      ...headers,
      ...newHeaders,
    },
  };
});

const batchLink = new BatchHttpLink({ uri: config.apiUrlFull, batchInterval: 15, batchMax: 5 });

const uploadLink = createUploadLink({ headers: { 'Apollo-Require-Preflight': 'true' }, uri: config.apiUrlFull });

const cache = getCache();

const wsLink = new WebSocketLink(
  {
    url: config.wsUrlFull,
    keepAlive: 20_000,
  },
  {
    getStore: () => store,
    getLeaseManager,
    getAuthInstance: getInstance,
  }
);
const isUploadType = (type: TypeNode) => type.kind === 'NamedType' && type.name?.value === 'Upload';

// Adds graphql context to sentry
const sentryLink = new SentrySubscriptionLink({
  uri: config.apiUrlFull,
  attachBreadcrumbs: {
    includeError: true,
  },
});

const websocketOrHttp: (op: Operation) => boolean = ({ query }) => {
  const definition = getMainDefinition(query);

  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

const uploadOrBatchMutations: (op: Operation) => boolean = (operation) => {
  const main = getMainDefinition(operation.query);

  if (main.kind === 'OperationDefinition' && main.operation === 'query') {
    // Some queries take a long time to complete, so it's bettwe not to batch them for now
    return true;
  }

  if (main.kind === 'OperationDefinition' && main.operation === 'mutation') {
    const mutation = getOperationDefinition(operation.query);

    const variableDefs = mutation.variableDefinitions;

    for (let index = 0; index < variableDefs.length; index++) {
      const variable = variableDefs[index];
      if (isUploadType(variable.type) || (variable.type.kind === 'NonNullType' && isUploadType(variable.type.type))) {
        return true;
      }
    }
  }

  return false;
};

const link = from([
  sentryLink,
  authLink,
  errorLink,
  split(
    websocketOrHttp,
    wsLink,
    retryLink.concat(split(uploadOrBatchMutations, computeLink.concat(uploadLink), batchLink))
  ),
]);

export const apolloClient = new ApolloClient({
  link,
  cache,
});

export const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
  defaultOptions: {
    /**
     * Ignore data in responses with GraphQL errors.
     * For GraphQL errors that might have data, you would need errorPolicy "all" and a custom update handler
     */
    $query: {
      errorPolicy: 'none',
    },
    $loadingKey: 'loading',
  },
});
